import { toNumber } from 'lodash'
import { last, values } from 'ramda'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import CurrencyFormatter from '@/components/currency-formatter'
import { H4, Label } from '@/components/text'
import { Stack, Box } from '@/components/structure'
import Icon from '@/components/icon'
import Panel from '@/components/panel'
import config from '@/config'
import { show as showProcessingFeesModal } from '@/features/Payments/processing-fees-modal'
import { breakpoints, colors } from '@/styles'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const ContentWrapper = styled.div`
  max-width: 600px;
  width: calc(100% - 40px);
  margin: 0 20px 100px;
  text-align: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: calc(100% - 20px);
    margin: 0 10px 70px;
  }
`

const StyledPanel = styled(Panel)`
  text-align: left;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0px;
  }
`

const StyledRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-left: 0;
  margin-right: 0;
  ${props =>
    props.borderTop &&
    `
    border-top: 1px solid ${colors.grey};
    padding: 20px 0 0;
  `};
`

const StyledLabel = styled(props => <Label {...props} type="small" />)`
  ${props =>
    props.grey &&
    `
    color: ${colors.mediumGrey};
  `}
`

const StyledValue = styled(props => <Label {...props} />)`
  ${props =>
    props.grey &&
    `
    color: ${colors.mediumGrey};
  `}
`

const FeeInfoWrapper = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-left: 5px;
    cursor: pointer;
  }
`

const TenantPaymentHistory = ({ paymentData = {} }) => {
  const { t } = useTranslation()
  const hasDiscount = !!paymentData.discount_amount && toNumber(paymentData.discount_amount) > 0

  const paymentRequestId = paymentData.id
  const pdfUrl = `${config.BACKEND_SERVER}/payment_request/receipt/tenant/${paymentRequestId}/receipt.pdf`

  const transactionInfo = paymentData.transaction_info
  const isPadPayment = paymentData.request_type === 'pad'
  const isBitcoinPayment = !isPadPayment && transactionInfo.paid_with === 'crypto'
  const cryptoTransaction = last(values(paymentData.payment_transactions)) || {}
  const paidWithText = (() => {
    if (isPadPayment) return t('b.payment.view.pad_description.label', 'PAD (Pre-Authorized Debit)')
    if (isBitcoinPayment) return 'Bitcoin'
    return `${transactionInfo.card_type} •••• ${transactionInfo.last4}`
  })()
  const chargeId = isBitcoinPayment
    ? transactionInfo.coinbase_invoice_code
    : transactionInfo.charge_id

  if (!paymentData) return null

  return (
    <>
      <Wrapper>
        <ContentWrapper>
          <Box top={40} bottom={24} justify="center">
            <H4>{t('b.payment.view.payment_summary.title', 'Payment Summary')}</H4>
          </Box>
          <StyledPanel hSpace={24} vSpace={24}>
            <Stack gutter={20}>
              <StyledRow>
                <StyledLabel>{t('b.payment.view.status.label', 'Status')}</StyledLabel>
                <StyledValue>
                  {paymentData.state_machine === 'approved'
                    ? t('b.payment.view.successful.text', 'Successful')
                    : t('b.payment.view.unsuccessful.text', 'Unsuccessful')}
                </StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>
                  {t('b.payment.view.confirmation_number.label', 'Confirmation No.')}
                </StyledLabel>
                <StyledValue>{chargeId || '-'}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>{t('b.payment.view.sent_by.label', 'Sent by')}</StyledLabel>
                <StyledValue>{`${paymentData.tenant?.preferred_name} ${paymentData.tenant?.last_name}.`}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>{t('b.payment.view.sent_to.label', 'To')}</StyledLabel>
                <StyledValue>{`${paymentData.user?.preferred_name} ${paymentData.user?.last_name}.`}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>{t('b.payment.view.detail.label', 'Detail')}</StyledLabel>
                <StyledValue>{paymentData.description}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>
                  {t('b.payment.view.payment_method.label', 'Payment method')}
                </StyledLabel>
                <StyledValue>{paidWithText}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>
                  {t('b.payment.view.payment_amount.label', 'Payment amount')}
                </StyledLabel>
                <StyledValue data-testid="requested-value">
                  <CurrencyFormatter value={paymentData.requested_amount} showCents />
                </StyledValue>
              </StyledRow>
              {hasDiscount && (
                <StyledRow>
                  <StyledLabel>{t('b.payment.view.discount.label', 'Discount')}</StyledLabel>
                  <StyledValue data-testid="discount-value">
                    -
                    <CurrencyFormatter value={paymentData.discount_amount} showCents />
                  </StyledValue>
                </StyledRow>
              )}
              <StyledRow borderTop>
                <StyledLabel>{t('b.payment.view.subtotal.label', 'Subtotal')}</StyledLabel>
                <StyledValue data-testid="subtotal-value">
                  <CurrencyFormatter
                    value={paymentData.requested_amount - paymentData.discount_amount}
                    showCents
                  />
                </StyledValue>
              </StyledRow>
              <StyledRow>
                <FeeInfoWrapper>
                  <StyledLabel>
                    {t('b.payment.view.make_payment_processing_fees.text', 'Processing fees')}
                  </StyledLabel>
                  <Icon id="info_blue" width={16} height={16} onClick={showProcessingFeesModal} />
                </FeeInfoWrapper>
                <StyledValue data-testid="fee-value">
                  <CurrencyFormatter value={paymentData.fee} showCents />
                </StyledValue>
              </StyledRow>
              <StyledRow borderTop>
                <StyledLabel type="medium">
                  {t('b.payment.view.make_payment_total.label', 'Total')}
                </StyledLabel>
                <StyledValue data-testid="charged-value">
                  <CurrencyFormatter value={paymentData.charged} showCents />
                </StyledValue>
              </StyledRow>
              {isBitcoinPayment && (
                <>
                  <StyledRow>
                    <StyledLabel grey>
                      <Trans
                        i18nKey="b.payment.view.crypto_paid.label"
                        values={{
                          currencyType: cryptoTransaction.crypto_currency_paid
                        }}
                        defaults="{{ currencyType }} paid"
                      />
                    </StyledLabel>
                    <StyledValue grey type="small">
                      {Number(cryptoTransaction.crypto_amount_paid).toFixed(15)}
                    </StyledValue>
                  </StyledRow>
                  <StyledRow>
                    <StyledLabel grey>
                      {t('b.payment.view.exchange_rate.label', 'Exchange rate')}
                    </StyledLabel>
                    <StyledValue grey type="small">
                      1 {cryptoTransaction.currency} ={' '}
                      {Number(1 / cryptoTransaction.crypto_exchange_rate_paid).toFixed(10)}{' '}
                      {cryptoTransaction.crypto_currency_paid}
                    </StyledValue>
                  </StyledRow>
                </>
              )}
            </Stack>
          </StyledPanel>
        </ContentWrapper>
      </Wrapper>
      <DefaultButtonBar>
        <Button theme="primary" size="small" href={pdfUrl}>
          {t('b.payment.view.export_payment_receipt.button', 'Export')}
        </Button>
      </DefaultButtonBar>
    </>
  )
}

export default TenantPaymentHistory
